<template>
  <div v-if="!isFooterOnly">
    <form @submit.prevent="handleSubmit">
      <div class="field">
        <label class="label">Status</label>
        <div class="control">
          <div class="select">
            <select v-model="form.is_canceled">
              <option :value="false">Active</option>
              <option :value="true">Canceled</option>
            </select>
          </div>
        </div>
      </div>

      <div class="field">
        <label class="label">Start Date</label>
        <div class="control">
          <input
            type="datetime-local"
            class="input"
            v-model="dateLocal.start_date"
          />
        </div>
      </div>

      <div class="field">
        <label class="label">End Date</label>
        <div class="control">
          <input
            type="datetime-local"
            class="input"
            v-model="dateLocal.end_date"
          />
        </div>
      </div>

      <div class="field">
        <label class="label">Next Invoice Date</label>
        <div class="control">
          <input
            type="datetime-local"
            class="input"
            v-model="dateLocal.next_invoice_date"
          />
        </div>
      </div>

      <div class="field">
        <label class="label">Package</label>
        <div class="control">
          <div class="select">
            <select v-model.number="form.package_id">
              <option value="">Select Package</option>
              <option :value="1">Basic</option>
              <option :value="2">Pro</option>
              <option :value="3">Enterprise</option>
            </select>
          </div>
        </div>
      </div>

      <div class="field">
        <label class="label">User Count</label>
        <div class="control">
          <input
            type="number"
            class="input"
            v-model.number="form.features.user_count"
            min="1"
          />
        </div>
      </div>

      <div class="field">
        <label class="label">Snapshot Frequency (minutes)</label>
        <div class="control">
          <input
            type="number"
            class="input"
            v-model.number="form.features.snapshot_frequency"
            min="1"
          />
        </div>
      </div>

      <div class="field">
        <label class="label">Camera Count</label>
        <div class="control">
          <input
            type="number"
            class="input"
            v-model.number="form.features.camera_count"
            min="1"
          />
        </div>
      </div>

      <div class="field">
        <label class="label">Project Count</label>
        <div class="control">
          <input
            type="number"
            class="input"
            v-model.number="form.features.project_count"
            min="1"
          />
        </div>
      </div>

      <div class="field">
        <label class="label">Monthly Bulk Photo Downloads</label>
        <div class="control">
          <input type="number" class="input" v-model.number="form.features.monthly_bulk_photo_download_count" min="0" step="1" required />
        </div>
        <p class="help">Shared across the contract. Set 0 to disable bulk downloads.</p>
      </div>

      <div class="field">
        <label class="label">Monthly Video Count</label>
        <div class="control">
          <input
            type="number"
            class="input"
            v-model.number="form.features.monthly_video_count"
            min="1"
          />
        </div>
      </div>

      <div class="field">
        <div class="control">
          <label class="checkbox">
            <input type="checkbox" v-model="form.features.ai_detection" />
            AI Detection
          </label>
        </div>
      </div>

      <div class="field">
        <div class="control">
          <label class="checkbox">
            <input type="checkbox" v-model="form.features.use_raw" />
            RAW Upload (Pro Camera)
          </label>
        </div>
      </div>

      <div class="field">
        <label class="label">JPEG Quality (Pro Camera, 1-100)</label>
        <div class="control">
          <input
            type="number"
            class="input"
            v-model.number="form.features.jpeg_quality"
            min="1"
            max="100"
            placeholder="80"
          />
        </div>
      </div>

      <div class="field">
        <label class="label">Check-in Interval (Pro Camera, minutes)</label>
        <div class="control">
          <input
            type="number"
            class="input"
            v-model.number="form.features.checkin_minutes"
            min="1"
            max="10080"
            placeholder="120"
          />
        </div>
      </div>
    </form>
  </div>

  <div v-else class="buttons">
    <button class="button" @click="closeModal">Cancel</button>
    <button class="button is-primary" @click="triggerSubmit">
      Update Contract
    </button>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { httpInstance as http } from "@/plugins/http";
import toast from "@/utils/toast";

export default {
  name: "ModalEditContract",
  props: {
    modalData: {
      type: Object,
      default: () => ({}),
    },
    modalIndex: {
      type: Number,
      required: true,
    },
    isFooterOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      form: {
        id: null,
        start_date: "",
        end_date: { Valid: false, Time: "" },
        next_invoice_date: { Valid: false, Time: "" },
        features: {
          user_count: 1,
          camera_count: 1,
          project_count: 1,
          snapshot_frequency: 30,
          monthly_video_count: 1,
          monthly_bulk_photo_download_count: 1,
          ai_detection: false,
          use_raw: false,
          jpeg_quality: "",
          checkin_minutes: "",
        },
        is_canceled: false,
        package_id: "",
      },
      dateLocal: {
        start_date: "",
        end_date: "",
        next_invoice_date: "",
      },
    };
  },
  methods: {
    ...mapActions(["closeTopModal"]),
    closeModal() {
      this.closeTopModal();
    },
    triggerSubmit() {
      this.$root.$emit("edit-contract-submit");
    },
    formatDateForInput(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toISOString().slice(0, 16);
    },
    async handleSubmit() {
      try {
        if (!Number.isInteger(this.form.features.monthly_bulk_photo_download_count) || this.form.features.monthly_bulk_photo_download_count < 0) {
          toast.error("Bulk photo download limit must be a whole number of 0 or more");
          return;
        }
        const formData = new FormData();
        formData.append("id", this.form.id);

        const startDate = this.dateLocal.start_date
          ? `${this.dateLocal.start_date}:00Z`
          : "";
        formData.append("start_date", startDate);

        if (this.dateLocal.end_date) {
          const endDate = `${this.dateLocal.end_date}:00Z`;
          formData.append("end_date", endDate);
        }

        if (this.dateLocal.next_invoice_date) {
          const nextInvoiceDate = `${this.dateLocal.next_invoice_date}:00Z`;
          formData.append("next_invoice_date", nextInvoiceDate);
        }

        formData.append("user_count", this.form.features.user_count);
        formData.append(
          "snapshot_frequency",
          this.form.features.snapshot_frequency
        );
        formData.append("project_count", this.form.features.project_count);
        formData.append("camera_count", this.form.features.camera_count);
        formData.append(
          "monthly_video_count",
          this.form.features.monthly_video_count
        );
        formData.append("ai_detection", this.form.features.ai_detection);
        formData.append("use_raw", this.form.features.use_raw);
        if (this.form.features.jpeg_quality) {
          formData.append("jpeg_quality", this.form.features.jpeg_quality);
        }
        if (this.form.features.checkin_minutes) {
          formData.append("checkin_minutes", this.form.features.checkin_minutes);
        }
        formData.append("monthly_bulk_photo_download_count", this.form.features.monthly_bulk_photo_download_count);
        formData.append("is_canceled", this.form.is_canceled);
        formData.append("package_id", this.form.package_id);

        const response = await http.patch("contracts/", formData);

        if (response.status === 200) {
          toast.success("Contract updated successfully");
          this.closeModal();
          this.$root.$emit("contract-updated");
        }
      } catch (error) {
        console.error("Error updating contract:", error);
        toast.error("Error occurred while updating contract");
      }
    },
    initializeForm() {
      if (this.modalData) {
        this.form.id = this.modalData.id;
        this.form.start_date = this.modalData.start_date;
        this.form.end_date = this.modalData.end_date || { Valid: false, Time: "" };
        this.form.next_invoice_date = this.modalData.next_invoice_date || {
          Valid: false,
          Time: "",
        };
        this.form.features = {
          user_count: this.modalData.features?.user_count || 1,
          camera_count: this.modalData.features?.camera_count || 1,
          project_count: this.modalData.features?.project_count || 1,
          snapshot_frequency: this.modalData.features?.snapshot_frequency || 30,
          monthly_video_count: this.modalData.features?.monthly_video_count || 1,
          monthly_bulk_photo_download_count: Number(this.modalData.features?.monthly_bulk_photo_download_count ?? 1),
          ai_detection: this.modalData.features?.ai_detection || false,
          use_raw: this.modalData.features?.use_raw || false,
          jpeg_quality: this.modalData.features?.jpeg_quality || "",
          checkin_minutes: this.modalData.features?.checkin_minutes || "",
        };
        this.form.is_canceled = this.modalData.is_canceled || false;
        this.form.package_id = this.modalData.package_id || "";

        this.dateLocal.start_date = this.formatDateForInput(this.modalData.start_date);
        this.dateLocal.end_date = this.formatDateForInput(this.modalData.end_date?.Time);
        this.dateLocal.next_invoice_date = this.formatDateForInput(
          this.modalData.next_invoice_date?.Time
        );
      }
    },
  },
  watch: {
    modalData: {
      handler() {
        this.initializeForm();
      },
      immediate: true,
    },
  },
  mounted() {
    this.initializeForm();

    if (!this.isFooterOnly) {
      this.$root.$on("edit-contract-submit", this.handleSubmit);
    }
  },
  beforeDestroy() {
    if (!this.isFooterOnly) {
      this.$root.$off("edit-contract-submit", this.handleSubmit);
    }
  },
};
</script>

<style scoped>
.buttons {
  justify-content: flex-end;
}
</style>
